/**
 * phoneUtils.js — Helpers de normalisation des numéros de téléphone.
 *
 * Permet de :
 *  - splitter un numéro complet (ex: "+590690123456") en `{ indicatif, numero }`
 *  - utiliser ces deux parties dans les champs séparés du workflow
 *    (`indicatif_telephone` + `telephone`) sans afficher le préfixe deux fois.
 *
 * Le set d'indicatifs supportés est dérivé de PAYS_OPTIONS pour rester aligné
 * avec les drapeaux affichés. Triés du plus long au plus court pour éviter
 * qu'un préfixe court (+33) ne matche avant un long (+377).
 */
import { PAYS_OPTIONS } from '../constants';

const INDICATIFS_SUPPORTES = Array.from(
  new Set(PAYS_OPTIONS.map((p) => p.indicatif).filter(Boolean)),
).sort((a, b) => b.length - a.length);

/**
 * Décompose un numéro complet en indicatif + numéro local.
 * Tolère les espaces, points et tirets. Retourne null si aucun indicatif n'est
 * détecté (numéro déjà sans préfixe).
 *
 * @param {string} fullPhone
 * @returns {{ indicatif: string, numero: string } | null}
 */
export function splitPhoneNumber(fullPhone) {
  if (!fullPhone || typeof fullPhone !== 'string') return null;
  const cleaned = fullPhone.replace(/[\s.\-_]/g, '');
  if (!cleaned.startsWith('+')) return null;
  for (const ind of INDICATIFS_SUPPORTES) {
    if (cleaned.startsWith(ind)) {
      const numero = cleaned.slice(ind.length);
      return { indicatif: ind, numero };
    }
  }
  return null;
}

/**
 * Si le numéro du membre contient son indicatif (`+590...`), retourne le
 * numéro local seul. Sinon retourne le numéro inchangé.
 *
 * Sert à éviter un double affichage de l'indicatif quand le composant
 * `PhoneInputWithFlagSVG` affiche déjà le préfixe sélectionné.
 *
 * @param {string} phone — numéro brut du membre
 * @param {string} [knownIndicatif] — indicatif déjà sélectionné (priorité)
 * @returns {string} numéro local sans préfixe
 */
export function stripIndicatif(phone, knownIndicatif = null) {
  if (!phone || typeof phone !== 'string') return phone || '';
  const cleaned = phone.replace(/[\s.\-_]/g, '');
  // Si on connaît l'indicatif et qu'il est en préfixe, on le retire.
  if (knownIndicatif && cleaned.startsWith(knownIndicatif)) {
    return cleaned.slice(knownIndicatif.length);
  }
  const split = splitPhoneNumber(phone);
  return split ? split.numero : phone;
}

/**
 * Normalise une paire `{ indicatif, numero }` pour le formData :
 *  - Si `phone` contient déjà un indicatif → utilise celui-ci
 *  - Sinon → conserve `defaultIndicatif`
 *  - Le `telephone` retourné est TOUJOURS sans préfixe pour éviter le doublon.
 */
export function normalizePhoneForForm(phone, defaultIndicatif = '+33') {
  const split = splitPhoneNumber(phone);
  if (split) {
    return { indicatif_telephone: split.indicatif, telephone: split.numero };
  }
  return { indicatif_telephone: defaultIndicatif, telephone: phone || '' };
}
